import { useEffect, useState } from 'react';
import { AnalyticalRowFieldInput } from './AnalyticalRowFieldInput';
import { ApiError } from '../../lib/api';
import { analyticalTablesApi, findReferenceMirrorField, type AnalyticalTableDetail } from '../../lib/analyticalTablesApi';
import { layersApi, type RelatedAnalyticalRows } from '../../lib/layersApi';

/** One "Добавить" form for a related analytical table row — the reference
 * field (e.g. meter_feature_id on "Показания электроэнергии") is pre-set to
 * this object's own id and not shown, so from a meter's card adding a reading
 * is just filling in date/plan/fact, not a separate relationship step. */
function AddRelatedRowForm({
  tableId,
  fieldCode,
  featureId,
  featureTitle,
  onCancel,
  onSaved,
}: {
  tableId: string;
  fieldCode: string;
  featureId: string;
  featureTitle: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [detail, setDetail] = useState<AnalyticalTableDetail | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    analyticalTablesApi.getTable(tableId).then(setDetail).catch(() => setDetail(null));
  }, [tableId]);

  if (!detail) return <p className="mt-2 text-[12px] text-text-secondary">Загрузка…</p>;

  const referenceField = detail.fields.find((f) => f.code === fieldCode);
  const mirrorField = referenceField ? findReferenceMirrorField(detail.fields, referenceField) : undefined;
  const visibleFields = detail.fields.filter((f) => f.code !== fieldCode && f.code !== mirrorField?.code);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const attributes: Record<string, unknown> = { [fieldCode]: featureId };
      if (mirrorField) attributes[mirrorField.code] = featureTitle;
      for (const f of visibleFields) {
        const v = values[f.code];
        if (v) attributes[f.code] = v;
      }
      await analyticalTablesApi.createRow(tableId, attributes);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить запись.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-lg border border-border-default bg-bg-surface-secondary p-3">
      <div className="grid grid-cols-2 gap-2">
        {visibleFields.map((f) => (
          <div key={f.code} className="flex flex-col gap-1">
            <label className="text-[11px] font-medium text-text-secondary">{f.name}</label>
            <AnalyticalRowFieldInput
              field={f}
              value={values[f.code] ?? ''}
              onChange={(v) => setValues((prev) => ({ ...prev, [f.code]: v }))}
              referenceOptions={[]}
            />
          </div>
        ))}
      </div>
      {error && <p className="text-[11px] font-medium text-status-danger">{error}</p>}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="text-[12px] font-medium text-text-secondary hover:text-text-primary">
          Отмена
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-action-primary px-3 py-1.5 text-[12px] font-medium text-text-inverse disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Сохраняем…' : 'Добавить'}
        </button>
      </div>
    </div>
  );
}

/** "Связанные записи" — for objects a analytical table refers to (see
 * app/routers/analytical_tables.py — e.g. «Показания электроэнергии» refers
 * to «Приборы учёта»): shows the matching rows and an inline add form, right
 * on the referenced object's own card. Without this, a reading could only be
 * added through Отчёты, with the meter picked from a plain dropdown instead
 * of being implicit from which card you're on. Renders nothing if no
 * analytical table references this feature's layer at all. */
export function RelatedRowsSection({
  layerId,
  featureId,
  featureTitle,
}: {
  layerId: string;
  featureId: string;
  featureTitle: string;
}) {
  const [groups, setGroups] = useState<RelatedAnalyticalRows[]>([]);
  const [addingFor, setAddingFor] = useState<string | null>(null);

  const refresh = () => {
    layersApi
      .getRelatedAnalyticalRows(layerId, featureId)
      .then(setGroups)
      .catch(() => setGroups([]));
  };

  useEffect(refresh, [layerId, featureId]);

  if (groups.length === 0) return null;

  return (
    <>
      {groups.map((g) => (
        <div key={g.table_id} className="mt-5">
          <p className="text-[11px] font-medium text-text-secondary">
            {g.table_name}
            {g.rows.length > 0 ? ` (${g.rows.length})` : ''}
          </p>
          {g.rows.length === 0 ? (
            <p className="mt-1.5 text-[13px] text-text-secondary">Нет</p>
          ) : (
            <div className="mt-1.5 flex flex-col gap-1">
              {g.rows.map((r) => (
                <p key={r.id} className="text-[13px] text-text-primary">
                  {r.name}
                </p>
              ))}
            </div>
          )}
          {addingFor === g.table_id ? (
            <AddRelatedRowForm
              tableId={g.table_id}
              fieldCode={g.field_code}
              featureId={featureId}
              featureTitle={featureTitle}
              onCancel={() => setAddingFor(null)}
              onSaved={() => {
                setAddingFor(null);
                refresh();
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingFor(g.table_id)}
              className="mt-1.5 text-[12px] font-medium text-action-primary hover:underline"
            >
              Добавить
            </button>
          )}
        </div>
      ))}
    </>
  );
}
